import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CalendarPlus,
  CalendarRange,
  Copy,
  FileText,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Printer,
  Sparkles,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { api, type EstadoLicenca, type MioloResumo } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';

export function MeusMiolos() {
  const { usuario } = useAuth();
  const navegar = useNavigate();

  const [miolos, setMiolos] = useState<MioloResumo[] | null>(null);
  const [licenca, setLicenca] = useState<EstadoLicenca | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [paraExcluir, setParaExcluir] = useState<MioloResumo | null>(null);
  /** O administrador bloqueou o acesso: consulta liberada, geração não. */
  const bloqueado = licenca?.bloqueado === true;

  const carregar = useCallback(async () => {
    try {
      const resposta = await api.listarMiolos();
      setMiolos(resposta.miolos);
      setErro(null);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível carregar seus miolos.');
      setMiolos([]);
    }

    try {
      setLicenca(await api.minhaLicenca());
    } catch {
      setLicenca(null);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const duplicar = async (miolo: MioloResumo, proximoAno = false) => {
    setOcupado(miolo.id);
    try {
      const resposta = await api.duplicarMiolo(miolo.id, proximoAno);
      await carregar();
      navegar(`/miolos/${resposta.miolo.id}`);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível duplicar.');
    } finally {
      setOcupado(null);
    }
  };

  const excluir = async () => {
    if (!paraExcluir) return;
    setOcupado(paraExcluir.id);
    try {
      await api.excluirMiolo(paraExcluir.id);
      setParaExcluir(null);
      await carregar();
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível excluir.');
    } finally {
      setOcupado(null);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Meus miolos</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Olá, {usuario?.nome}. Aqui ficam seus miolos salvos — reabra, ajuste, duplique e imprima quando
            quiser.
          </p>
        </div>
        {bloqueado ? (
          <Button
            disabled
            title="Seu acesso está bloqueado: fale com quem te atende para gerar novos miolos."
          >
            <Lock />
            Novo miolo
          </Button>
        ) : (
          <Button asChild>
            <Link to="/criar">
              <Plus />
              Novo miolo
            </Link>
          </Button>
        )}
      </header>

      {bloqueado ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
          <Lock className="size-3.5 shrink-0" />
          <span className="font-medium">Acesso bloqueado.</span>
          <span>
            Seus miolos continuam salvos para consulta, mas não é possível gerar novos nem duplicar. Fale com quem
            te atende para liberar de novo.
          </span>
        </div>
      ) : licenca ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3 text-xs">
          <span className="flex items-center gap-1.5 font-medium">
            <Sparkles className="size-3.5 text-primary" />
            {licenca.licenca ? `Licença ${licenca.licenca.plano.nome}` : 'Sem licença ativa'}
          </span>
          <span className="text-muted-foreground">
            {licenca.uso.limiteDownloads === null
              ? `${licenca.uso.downloads} download(s) · ilimitado`
              : licenca.uso.limiteDownloads === 0
                ? 'sem miolos de agenda (só documentos)'
                : `${licenca.uso.downloads} de ${licenca.uso.limiteDownloads} downloads usados`}
          </span>
          <Button asChild variant="contorno" tamanho="pequeno" className="ml-auto">
            <Link to="/planos">{licenca.licenca ? 'Mudar plano' : 'Ver planos e preços'}</Link>
          </Button>
        </div>
      ) : null}

      {erro ? (
        <p className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
          <TriangleAlert className="size-3.5" />
          {erro}
        </p>
      ) : null}

      {miolos === null ? (
        <div className="flex min-h-[30vh] items-center justify-center gap-3 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
          <span className="text-sm">Carregando...</span>
        </div>
      ) : miolos.length === 0 ? (
        <Card>
          <CardHeader>
            <span className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
              <FileText className="size-5" />
            </span>
            <CardTitle className="pt-1">Sua primeira agenda começa aqui</CardTitle>
            <CardDescription>
              O assistente monta o miolo inteiro em poucos passos: formato, dias por página, cores com
              teoria das cores aplicada, estilo de conteúdo e seus dados pessoais. Dá para começar de um
              modelo pronto ou do zero.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/criar">
                <Plus />
                Criar meu primeiro miolo
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <ul className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {miolos.map((miolo) => {
            const resumo = miolo.resumo;
            const avisosAtencao = (miolo.avisos ?? []).filter((a) => a.nivel === 'ATENCAO').length;
            /*
             * O ano seguinte sai do próprio miolo: a agenda datada informa os anos
             * que apresenta, e a cópia avança o último deles em um.
             */
            const anoSeguinte = (resumo?.anosDaVisao?.at(-1) ?? new Date().getFullYear()) + 1;
            return (
              <li key={miolo.id}>
                <Card className="flex h-full flex-col">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <CardTitle className="min-w-0">{miolo.nome}</CardTitle>
                      <span
                        className="mt-0.5 size-6 shrink-0 rounded-md border border-black/10"
                        style={{ background: resumo?.corPrincipal ?? '#94a3b8' }}
                        aria-hidden
                      />
                    </div>
                    <CardDescription className="flex flex-wrap items-center gap-1.5 pt-1">
                      <Badge>{resumo?.formato ?? '—'}</Badge>
                      {resumo ? <Badge>{resumo.diasPorPagina} dia(s)/página</Badge> : null}
                      {resumo ? <Badge tom="principal">{resumo.totalPaginas} páginas</Badge> : null}
                      {avisosAtencao > 0 ? <Badge tom="atencao">{avisosAtencao} alerta(s)</Badge> : null}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="mt-auto space-y-3">
                    <dl className="space-y-1 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <CalendarRange className="size-3.5" />
                        <span>{resumo?.periodo ?? 'período não informado'}</span>
                      </div>
                      <div>
                        <span className="capitalize">{resumo?.estilo ?? ''}</span>
                        {resumo ? ` · harmonia ${resumo.esquemaCores.replace(/_/g, ' ').toLowerCase()}` : ''}
                      </div>
                      <div>
                        Atualizado em{' '}
                        {new Date(miolo.atualizadoEm).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </div>
                    </dl>

                    <div className="flex flex-wrap gap-2">
                      <Button asChild tamanho="pequeno" className="flex-1">
                        <Link to={`/miolos/${miolo.id}`}>
                          <Printer />
                          Abrir e imprimir
                        </Link>
                      </Button>
                      <Button asChild variant="contorno" tamanho="pequeno">
                        <Link to={`/miolos/${miolo.id}/editar`}>
                          <Pencil />
                          Editar
                        </Link>
                      </Button>
                      <Button
                        variant="fantasma"
                        tamanho="icone"
                        aria-label="Duplicar"
                        title={bloqueado ? 'Acesso bloqueado: não é possível duplicar.' : undefined}
                        disabled={ocupado === miolo.id || bloqueado}
                        onClick={() => void duplicar(miolo)}
                      >
                        <Copy />
                      </Button>
                      {/*
                        Quem tem agenda datada quer a do ano seguinte, com o mesmo
                        desenho: é o caso de todo fim de ano. Só aparece em miolo
                        datado — em permanente não há ano para avançar.
                      */}
                      {resumo?.datado ? (
                        <Button
                          variant="fantasma"
                          tamanho="icone"
                          aria-label={`Duplicar para ${anoSeguinte}`}
                          title={
                            bloqueado
                              ? 'Acesso bloqueado: não é possível duplicar.'
                              : `Duplicar para ${anoSeguinte}, com o mesmo desenho`
                          }
                          disabled={ocupado === miolo.id || bloqueado}
                          onClick={() => void duplicar(miolo, true)}
                        >
                          <CalendarPlus />
                        </Button>
                      ) : null}
                      <Button
                        variant="fantasma"
                        tamanho="icone"
                        aria-label="Excluir"
                        disabled={ocupado === miolo.id}
                        onClick={() => setParaExcluir(miolo)}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog open={Boolean(paraExcluir)} onOpenChange={(aberto) => (aberto ? undefined : setParaExcluir(null))}>
        {paraExcluir ? (
          <DialogContent
            titulo="Excluir este miolo?"
            descricao={`“${paraExcluir.nome}” será removido da sua área. Essa ação não pode ser desfeita.`}
          >
            <div className="flex justify-end gap-2">
              <Button variant="contorno" onClick={() => setParaExcluir(null)}>
                Cancelar
              </Button>
              <Button variant="destrutivo" onClick={() => void excluir()} disabled={ocupado === paraExcluir.id}>
                {ocupado === paraExcluir.id ? <Loader2 className="animate-spin" /> : <Trash2 />}
                Excluir
              </Button>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
}
